import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Loader2, Search, Unlink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { PROCEDURE_TYPE_LABELS, type ProcedureTemplateListItem, type ProcedureType } from '@/types/cgt'

/** A template as the dialog hands it back. */
export type ConnectedTemplate = Pick<ProcedureTemplateListItem, 'id' | 'name'>

interface ConnectTemplateDialogProps {
  open: boolean
  onClose: () => void
  /** What the template is being connected to, in a sentence. */
  description: string
  /** Only active templates of this type are offered. */
  templateType: ProcedureType
  /** The template connected now, or null. */
  currentTemplateId: string | null
  /** Connect (or replace with) `template`. Rejecting shows its message in the dialog. */
  onConnect: (template: ConnectedTemplate) => void | Promise<void>
  onDisconnect: () => void | Promise<void>
  /** Shown above the footer — e.g. what the connection just changed. */
  note?: ReactNode
}

/**
 * Connect / replace / disconnect a procedure TEMPLATE — the one dialog behind
 * every "Connect Procedure Template" in the app: a material's Inventory gate,
 * and an equipment or room cycle's procedure (Cleaning module, z8nrz7ewt5).
 * The candidate list is every active template of `templateType`, which is also
 * where the connected one is looked up, so it costs one fetch.
 */
export function ConnectTemplateDialog({
  open,
  onClose,
  description,
  templateType,
  currentTemplateId,
  onConnect,
  onDisconnect,
  note,
}: ConnectTemplateDialogProps) {
  const [templates, setTemplates] = useState<ProcedureTemplateListItem[]>([])
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pending, setPending] = useState<'connect' | 'disconnect' | null>(null)
  const typeLabel = PROCEDURE_TYPE_LABELS[templateType]

  useEffect(() => {
    if (!open) return
    setActionError(null)
    setSelectedId(null)
    setQuery('')
    setFetching(true)
    setFetchError(null)
    procedureTemplatesApi
      .list({ offerable: true })
      .then((items) => setTemplates(items.filter((t) => t.type === templateType)))
      .catch((e) => setFetchError(e instanceof Error ? e.message : 'Failed to load procedure templates.'))
      .finally(() => setFetching(false))
  }, [open, templateType])

  const connectedTemplate = useMemo(
    () => templates.find((t) => t.id === currentTemplateId) ?? null,
    [templates, currentTemplateId],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return templates
    return templates.filter((t) => t.name.toLowerCase().includes(q) || t.productCode?.toLowerCase().includes(q))
  }, [templates, query])

  async function run(kind: 'connect' | 'disconnect', action: () => void | Promise<void>, failure: string) {
    setActionError(null)
    setPending(kind)
    try {
      await action()
      // Disarm the button: what was selected is now what is connected.
      if (kind === 'connect') setSelectedId(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : failure)
    } finally {
      setPending(null)
    }
  }

  function handleConnect() {
    const template = templates.find((t) => t.id === selectedId)
    if (!template) return
    void run('connect', () => onConnect({ id: template.id, name: template.name }), 'Could not connect the procedure template.')
  }

  function handleDisconnect() {
    void run('disconnect', onDisconnect, 'Could not disconnect the procedure template.')
  }

  const busy = pending !== null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Connect Procedure Template</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {connectedTemplate ? (
          <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'>
            <div className='min-w-0'>
              <p className='text-xs uppercase tracking-wide text-gray-400'>Currently connected</p>
              <p className='truncate text-sm font-medium text-gray-800'>{connectedTemplate.name}</p>
            </div>
            <Button
              size='sm'
              variant='outline'
              className='shrink-0 gap-1 text-red-600 hover:bg-red-50 hover:text-red-700'
              disabled={busy}
              onClick={handleDisconnect}
            >
              {pending === 'disconnect' ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <Unlink className='h-3.5 w-3.5' />
              )}
              Disconnect
            </Button>
          </div>
        ) : currentTemplateId && !fetching ? (
          // Connected, but the template did not come back in this type's list —
          // e.g. it was retyped or inactivated since. Still offer Disconnect.
          <div className='flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2'>
            <p className='text-sm text-amber-800'>
              Connected to procedure template {currentTemplateId}, which is no longer an active {typeLabel}-type
              template.
            </p>
            <Button
              size='sm'
              variant='outline'
              className='shrink-0 gap-1 text-red-600 hover:bg-red-50 hover:text-red-700'
              disabled={busy}
              onClick={handleDisconnect}
            >
              <Unlink className='h-3.5 w-3.5' />
              Disconnect
            </Button>
          </div>
        ) : null}

        <div className='relative'>
          <Search className='pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
          <input
            type='text'
            placeholder='Search by template name or product code…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className='h-9 w-full rounded-md border bg-background pl-9 pr-3 text-sm outline-none focus:ring-1 focus:ring-ring'
          />
        </div>

        <div className='max-h-64 overflow-auto rounded-md border'>
          {fetching ? (
            <div className='flex items-center justify-center py-10'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          ) : fetchError ? (
            <p className='py-8 text-center text-sm text-red-600'>{fetchError}</p>
          ) : filtered.length === 0 ? (
            <p className='py-8 text-center text-sm text-muted-foreground'>
              No {typeLabel}-type procedure templates found.
            </p>
          ) : (
            <ul>
              {filtered.map((t) => (
                <li key={t.id}>
                  <button
                    type='button'
                    onClick={() => setSelectedId(t.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 border-b border-gray-50 px-3 py-2 text-left text-sm last:border-0 hover:bg-gray-50',
                      selectedId === t.id && 'bg-accent hover:bg-accent',
                      t.id === connectedTemplate?.id && 'font-medium',
                    )}
                  >
                    <span className='min-w-0 truncate'>{t.name}</span>
                    {t.id === connectedTemplate?.id && (
                      <span className='shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700'>
                        Connected
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {actionError && <p className='text-sm text-red-600'>{actionError}</p>}
        {note}

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button disabled={!selectedId || selectedId === connectedTemplate?.id || busy} onClick={handleConnect}>
            {pending === 'connect' && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {connectedTemplate ? 'Replace' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
